/**
 * <s-product-image-zoom> — Product image viewer (lightbox).
 *
 * Opens one full-screen viewer for every product image with a counter,
 * zoom in / zoom out, fullscreen, close, previous / next, click or double-tap
 * to zoom, drag to pan while zoomed, swipe between images, mouse-wheel zoom,
 * and keyboard support (arrows, + / -, Esc).
 *
 * Required markup:
 *   [data-lightbox-open="<index>"]   — gallery trigger; index is the image position (0-based).
 *   dialog[data-lightbox]            — the viewer dialog.
 *   [data-lightbox-track]            — flex row holding one slide per image.
 *   [data-lightbox-slide]            — one per image, in order.
 *   img[data-lightbox-image]         — the image inside each slide.
 *   [data-lightbox-counter][data-template] — counter text; template uses __i__ and __n__.
 *   [data-lightbox-action]           — buttons: prev, next, zoom-in, zoom-out, fullscreen, close.
 */
const MAX_SCALE = 4;
const STEP = 1;

class SProductImageZoom extends HTMLElement {
  #ac;
  #dialog;
  #track;
  #slides = [];
  #images = [];
  #counter;
  #index = 0;
  #scale = 1;
  #tx = 0;
  #ty = 0;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog[data-lightbox]');
    if (!this.#dialog) return;
    this.#track = this.#dialog.querySelector('[data-lightbox-track]');
    this.#slides = [...this.#dialog.querySelectorAll('[data-lightbox-slide]')];
    this.#images = this.#slides.map((slide) => slide.querySelector('img[data-lightbox-image]'));
    this.#counter = this.#dialog.querySelector('[data-lightbox-counter]');

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', this.#onOpenClick, opts);
    this.addEventListener('pointerover', this.#onPreloadIntent, opts);
    this.addEventListener('focusin', this.#onPreloadIntent, opts);
    this.#dialog.addEventListener('click', this.#onDialogClick, opts);
    this.#dialog.addEventListener('keydown', this.#onKeydown, opts);
    this.#dialog.addEventListener('close', this.#onClose, opts);
    this.#dialog.addEventListener('wheel', this.#onWheel, { ...opts, passive: false });
    this.#dialog.addEventListener('pointerdown', this.#onPointerDown, opts);
    this.#dialog.addEventListener('pointermove', this.#onPointerMove, opts);
    this.#dialog.addEventListener('pointerup', this.#onPointerUp, opts);
    this.#dialog.addEventListener('pointercancel', this.#onPointerUp, opts);
    document.addEventListener('fullscreenchange', this.#syncFullscreen, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /* ---------- open / navigate ---------- */

  #onOpenClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-lightbox-open]');
    if (!trigger || !this.contains(trigger)) return;
    event.preventDefault();
    this.open(Number(trigger.getAttribute('data-lightbox-open')) || 0);
  };

  #onPreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-lightbox-open]');
    if (!trigger) return;
    this.#warm(Number(trigger.getAttribute('data-lightbox-open')) || 0);
  };

  open(index) {
    if (!this.#images.length) return;
    this.#go(index, false);
    if (!this.#dialog.open) this.#dialog.showModal();
  }

  #go(index, animate = true) {
    const count = this.#images.length;
    this.#resetZoom();
    this.#index = (index + count) % count;
    this.#track.style.transition = animate ? '' : 'none';
    this.#track.style.transform = `translateX(${this.#index * -100}%)`;
    if (!animate) {
      void this.#track.offsetWidth;
      this.#track.style.transition = '';
    }
    this.#warm(this.#index);
    this.#warm(this.#index + 1);
    this.#warm(this.#index - 1);
    this.#slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== this.#index)));
    if (this.#counter) {
      const template = this.#counter.getAttribute('data-template') || '__i__ / __n__';
      this.#counter.textContent = template.replace('__i__', String(this.#index + 1)).replace('__n__', String(count));
    }
  }

  #warm(index) {
    const count = this.#images.length;
    const image = this.#images[(index + count) % count];
    if (image && image.loading !== 'eager') image.loading = 'eager';
  }

  /* ---------- toolbar ---------- */

  #onDialogClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-lightbox-action]');
    if (button) {
      const action = button.getAttribute('data-lightbox-action');
      if (action === 'prev') this.#go(this.#index - 1);
      if (action === 'next') this.#go(this.#index + 1);
      if (action === 'zoom-in') this.#zoomTo(this.#scale + STEP);
      if (action === 'zoom-out') this.#zoomTo(this.#scale - STEP);
      if (action === 'fullscreen') this.#toggleFullscreen();
      if (action === 'close') this.#dialog.close();
      return;
    }
    const image = event.target.closest('img[data-lightbox-image]');
    if (image && this.#lastPointerType === 'mouse' && !this.#gesture?.moved) {
      if (this.#scale > 1) {
        this.#resetZoom();
      } else {
        this.#zoomTo(2, event.clientX, event.clientY);
      }
    }
  };

  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') this.#go(this.#index - 1);
    else if (event.key === 'ArrowRight') this.#go(this.#index + 1);
    else if (event.key === '+' || event.key === '=') this.#zoomTo(this.#scale + STEP);
    else if (event.key === '-' || event.key === '_') this.#zoomTo(this.#scale - STEP);
    else return;
    event.preventDefault();
  };

  #onClose = () => {
    this.#resetZoom();
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  };

  #toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (this.#dialog.requestFullscreen) {
      this.#dialog.requestFullscreen().catch(() => {});
    }
  }

  #syncFullscreen = () => {
    this.#dialog.toggleAttribute('data-fullscreen', document.fullscreenElement === this.#dialog);
  };

  /* ---------- zoom & pan ---------- */

  #current() {
    return this.#images[this.#index];
  }

  #zoomTo(scale, clientX, clientY, instant = false) {
    const image = this.#current();
    if (!image) return;
    const next = Math.min(MAX_SCALE, Math.max(1, scale));
    if (next === 1) {
      this.#resetZoom();
      return;
    }
    if (clientX !== undefined) {
      const rect = image.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      // Keep the point under the cursor fixed while scaling around the image centre.
      const ratio = next / this.#scale;
      this.#tx += (clientX - cx) * (1 - ratio);
      this.#ty += (clientY - cy) * (1 - ratio);
    } else {
      const ratio = next / this.#scale;
      this.#tx *= ratio;
      this.#ty *= ratio;
    }
    this.#scale = next;
    this.#apply(instant);
  }

  #resetZoom() {
    this.#scale = 1;
    this.#tx = 0;
    this.#ty = 0;
    this.#apply();
  }

  /* Limit panning to the visible photo itself (not the empty space around it). */
  #clamp() {
    const image = this.#current();
    if (!image) return;
    const slide = this.#slides[this.#index];
    const boxW = image.offsetWidth;
    const boxH = image.offsetHeight;
    const ratio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : boxW / (boxH || 1);
    let w = boxW;
    let h = boxH;
    if (boxW / (boxH || 1) > ratio) w = boxH * ratio;
    else h = boxW / ratio;
    const maxX = Math.max(0, (w * this.#scale - slide.clientWidth) / 2);
    const maxY = Math.max(0, (h * this.#scale - slide.clientHeight) / 2);
    this.#tx = Math.min(maxX, Math.max(-maxX, this.#tx));
    this.#ty = Math.min(maxY, Math.max(-maxY, this.#ty));
  }

  #apply(instant = false, clamp = true) {
    const image = this.#current();
    if (!image) return;
    if (clamp) this.#clamp();
    image.style.transition = instant ? 'none' : '';
    image.style.transform = this.#scale === 1 && this.#tx === 0 && this.#ty === 0 ? '' : `translate3d(${this.#tx}px, ${this.#ty}px, 0) scale(${this.#scale})`;
    this.#dialog.toggleAttribute('data-zoomed', this.#scale > 1);
    const zoomIn = this.#dialog.querySelector('[data-lightbox-action="zoom-in"]');
    const zoomOut = this.#dialog.querySelector('[data-lightbox-action="zoom-out"]');
    if (zoomIn) zoomIn.disabled = this.#scale >= MAX_SCALE;
    if (zoomOut) zoomOut.disabled = this.#scale <= 1;
  }

  /* Trackpad pinch arrives as ctrl+wheel with small deltas; mouse wheels give big steps. */
  #onWheel = (event) => {
    if (!this.#dialog.open) return;
    event.preventDefault();
    const factor = Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.0025));
    this.#zoomTo(this.#scale * factor, event.clientX, event.clientY, true);
  };

  /* ---------- touch & mouse gestures ----------
     One finger: swipe between images (the image follows the finger) or pan when zoomed.
     Two fingers: pinch to zoom around the fingers. Double-tap toggles zoom on touch;
     a mouse click toggles zoom. */

  #pointers = new Map();
  #gesture = null;
  #lastTap = null;
  #lastPointerType = 'mouse';

  #onPointerDown = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('[data-lightbox-action]')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.#lastPointerType = event.pointerType;
    try {
      this.#dialog.setPointerCapture(event.pointerId);
    } catch (e) {}
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.#pointers.size === 2) {
      this.#startPinch();
    } else if (this.#pointers.size === 1) {
      this.#gesture = {
        type: 'drag',
        x: event.clientX,
        y: event.clientY,
        tx: this.#tx,
        ty: this.#ty,
        time: event.timeStamp,
        moved: false,
        axis: null
      };
    }
  };

  #startPinch() {
    const image = this.#current();
    if (!image) return;
    this.#snapTrack();
    const [a, b] = [...this.#pointers.values()];
    const slide = this.#slides[this.#index].getBoundingClientRect();
    this.#gesture = {
      type: 'pinch',
      d0: Math.hypot(b.x - a.x, b.y - a.y) || 1,
      s0: this.#scale,
      tx0: this.#tx,
      ty0: this.#ty,
      mx0: (a.x + b.x) / 2,
      my0: (a.y + b.y) / 2,
      cx: slide.left + slide.width / 2,
      cy: slide.top + slide.height / 2,
      moved: true
    };
  }

  #onPointerMove = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const g = this.#gesture;
    if (!g) return;

    if (g.type === 'pinch' && this.#pointers.size >= 2) {
      const [a, b] = [...this.#pointers.values()];
      const d = Math.hypot(b.x - a.x, b.y - a.y);
      const mx = (a.x + b.x) / 2;
      const my = (a.y + b.y) / 2;
      const scale = Math.min(MAX_SCALE * 1.15, Math.max(0.85, g.s0 * (d / g.d0)));
      // Keep the point between the fingers under the fingers while scaling and moving.
      const ux = (g.mx0 - g.cx - g.tx0) / g.s0;
      const uy = (g.my0 - g.cy - g.ty0) / g.s0;
      this.#scale = scale;
      this.#tx = mx - g.cx - ux * scale;
      this.#ty = my - g.cy - uy * scale;
      this.#apply(true, false);
      return;
    }

    if (g.type !== 'drag') return;
    const dx = event.clientX - g.x;
    const dy = event.clientY - g.y;
    if (!g.moved && Math.hypot(dx, dy) > 6) {
      g.moved = true;
      g.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (!g.moved) return;
    if (this.#scale > 1) {
      this.#tx = g.tx + dx;
      this.#ty = g.ty + dy;
      this.#apply(true);
    } else if (g.axis === 'x' && this.#images.length > 1) {
      this.#track.style.transition = 'none';
      this.#track.style.transform = `translateX(calc(${this.#index * -100}% + ${dx}px))`;
    }
  };

  #onPointerUp = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    const start = this.#pointers.get(event.pointerId);
    this.#pointers.delete(event.pointerId);
    const g = this.#gesture;
    const cancelled = event.type === 'pointercancel';

    if (g && g.type === 'pinch') {
      if (this.#pointers.size === 1) {
        // One finger left: continue as a pan from here.
        const [rest] = [...this.#pointers.values()];
        this.#settleZoom();
        this.#gesture = { type: 'drag', x: rest.x, y: rest.y, tx: this.#tx, ty: this.#ty, time: event.timeStamp, moved: true, axis: 'x' };
      } else if (this.#pointers.size === 0) {
        this.#settleZoom();
        this.#gesture = { type: 'done', moved: true };
        this.#clearGestureSoon();
      }
      return;
    }

    if (!g || g.type !== 'drag' || this.#pointers.size > 0) return;
    const dx = (cancelled ? start.x : event.clientX) - g.x;
    const dt = Math.max(1, event.timeStamp - g.time);

    if (g.moved && this.#scale === 1 && g.axis === 'x' && this.#images.length > 1) {
      const width = this.#slides[this.#index].clientWidth || window.innerWidth;
      const fast = Math.abs(dx) / dt > 0.4 && Math.abs(dx) > 20;
      if (!cancelled && (Math.abs(dx) > width * 0.18 || fast)) {
        this.#go(this.#index + (dx < 0 ? 1 : -1));
      } else {
        this.#snapTrack(true);
      }
    } else if (g.moved && this.#scale > 1) {
      this.#apply();
    } else if (!g.moved && !cancelled && event.pointerType !== 'mouse') {
      this.#handleTap(event);
    }
    this.#gesture = { type: 'done', moved: g.moved };
    this.#clearGestureSoon();
  };

  #handleTap(event) {
    if (!event.target.closest('img[data-lightbox-image]')) return;
    const now = event.timeStamp;
    const last = this.#lastTap;
    if (last && now - last.time < 320 && Math.hypot(event.clientX - last.x, event.clientY - last.y) < 30) {
      this.#lastTap = null;
      if (this.#scale > 1) this.#resetZoom();
      else this.#zoomTo(2.5, event.clientX, event.clientY);
    } else {
      this.#lastTap = { time: now, x: event.clientX, y: event.clientY };
    }
  }

  /* After a pinch, spring back into range: below 1 resets, above max eases back. */
  #settleZoom() {
    if (this.#scale <= 1.05) {
      this.#resetZoom();
    } else if (this.#scale > MAX_SCALE) {
      const ratio = MAX_SCALE / this.#scale;
      this.#scale = MAX_SCALE;
      this.#tx *= ratio;
      this.#ty *= ratio;
      this.#apply();
    } else {
      this.#apply();
    }
  }

  #snapTrack(animate = false) {
    this.#track.style.transition = animate ? '' : 'none';
    this.#track.style.transform = `translateX(${this.#index * -100}%)`;
  }

  #clearGestureSoon() {
    setTimeout(() => {
      if (this.#gesture && this.#gesture.type === 'done') this.#gesture = null;
    }, 50);
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
